/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  AutoCompleteSelectEvent,
  AutoCompleteCompleteEvent,
} from 'primereact/autocomplete';
import { useShipPage } from '@/hooks/Dashboard/useShipPage';
import { formatDateAndTime } from '@/utils/formatDate';
import {
  DualSymbolCard,
  DualTextCard,
  SingleMetricCard,
  SingleMetricCardWithIcon,
} from '@/components/Cards';
import { ThickBarChartWithoutAxes } from '@/components/Charts';
import { DropdownField, AutoCompleteField, LoadingSpinner } from '@/components';
import { getCurrentDateTime } from '@/utils/getCurrentDateTime';
import { useIsMobile } from '@/hooks';
import ShipChartCard from './components/ShipChartCard/ShipChartCard';
import cardIcon from '../../../../../assets/icons/dashboard/card-1-single-metric.svg';
import cardIcon2 from '../../../../../assets/icons/dashboard/card-2-single-metric.svg';
import cardIcon3 from '../../../../../assets/icons/dashboard/card-3-single-metric.svg';
import shipIconChartCard from '../../../../../assets/icons/dashboard/ship-chart-card.png';
import dualTextCardIcon from '../../../../../assets/icons/dashboard/tempo-icon.svg';
import co2Icon from '../../../../../assets/icons/dashboard/OcupationCard1.svg';
import styles from './ShipPage.module.scss';

interface Viaje {
  label: string;
  value: string;
}

export const ShipPage = () => {
  const {
    armadoresListForDropdown,
    currentArmador,
    viajesListForDropdown,
    loading,
    setCurrentArmador,
    shipPageData,
    setCurrentViaje,
    loadingArmadores,
    loadingPageData,
    loadingViajes,
    currentViaje,
  } = useShipPage();
  const isMobile = useIsMobile();

  const { t } = useTranslation('dashboardPage');
  const [viajes, setViajes] = useState<Viaje[]>([]);
  const [selectedViaje, setSelectedViaje] = useState<Viaje | null>(null);
  const [filteredViajes, setFilteredViajes] = useState<Viaje[]>();

  const lastUpdate = useMemo(() => {
    const now = new Date();
    const lastUpdateDate = new Date();

    if (now.getHours() < 12) {
      lastUpdateDate.setDate(lastUpdateDate.getDate() - 1);
      lastUpdateDate.setHours(23, 59, 59, 999);
    } else {
      lastUpdateDate.setHours(12, 0, 0, 0);
    }

    return getCurrentDateTime(lastUpdateDate);
  }, []);

  useEffect(() => {
    setSelectedViaje(currentViaje);
  }, [currentViaje]);

  useEffect(() => {
    if (!currentArmador.value && armadoresListForDropdown?.length) {
      setCurrentArmador({
        value: armadoresListForDropdown[0].value,
        name: armadoresListForDropdown[0].name,
      });
    }
  }, [
    armadoresListForDropdown,
    currentArmador.value,
    currentArmador.name,
    setCurrentArmador,
  ]);

  const handleArmadorChange = (e: {
    value: string | null;
    target?: { name: string; value?: unknown };
  }) => {
    setCurrentArmador({
      value: (e.target?.value as string) ?? '',
      name: e.target?.name ?? currentArmador.name,
    });
  };

  const search = (event: AutoCompleteCompleteEvent) => {
    let filteredViajes2;

    if (!event.query.trim().length) {
      filteredViajes2 = [...viajes];
    } else {
      filteredViajes2 = viajes.filter((viaje) => {
        return viaje.label.toLowerCase().startsWith(event.query.toLowerCase());
      });
    }

    setFilteredViajes(filteredViajes2);
  };

  const handleTripChange = (event: AutoCompleteSelectEvent) => {
    const tripName = event.value;
    setSelectedViaje(tripName);

    const code = viajesListForDropdown?.find(
      (trip) => trip.value === tripName?.value
    );

    if (code) {
      setCurrentViaje({
        value: tripName?.value ?? '',
        label: tripName?.label ?? '',
      });
    }
  };
  useEffect(() => {
    if (viajesListForDropdown) {
      setViajes(viajesListForDropdown);
    }
  }, [viajesListForDropdown]);

  /*
  useEffect(() => {
    if (viajesListForDropdown) {
      setSelectedViaje(viajesListForDropdown[0]);
    }
  }, [viajesListForDropdown]);
  */
  if (loading)
    return (
      <div style={{ overflow: 'hidden' }} className={styles.dashboard}>
        <LoadingSpinner className={styles.spinner} />
      </div>
    );

  return (
    <>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div className={styles.filterAndUpdatedAtContainer}>
            <DropdownField
              id="armador"
              name="armador"
              options={armadoresListForDropdown}
              value={currentArmador.value}
              label={t('shipPage.armador')}
              className={styles.selectDropdown}
              placeholder={t('shipPage.seleccionar_armador')}
              onChange={handleArmadorChange}
            />
            <p className={styles.lastUpdate}>
              {t('shipPage.ultima_actualizacion')} <span>{lastUpdate}</span>
            </p>
          </div>
        </div>
      </div>
      <div className={styles.dashboard}>
        <div className={styles.leftColumn}>
          <div className={classNames(styles.inputCard)}>
            <AutoCompleteField
              placeholder={t('shipPage.ingrese_buque_viaje')}
              label={t('shipPage.ingrese_buque_viaje')}
              field="label"
              legend={t('shipPage.legend')}
              value={selectedViaje}
              suggestions={filteredViajes}
              completeMethod={search}
              dropdown
              onSelect={handleTripChange}
              className={styles.autoComplete}
              labelClassname={styles.labelAutocomplete}
              isLoading={loadingArmadores || loadingPageData || loadingViajes}
            />
          </div>

          {viajesListForDropdown && viajesListForDropdown.length > 0 ? (
            <>
              <SingleMetricCardWithIcon
                title={t('shipPage.productividad_buque')}
                detail={
                  shipPageData?.productividadBuque?.productividadBuque.toString() ??
                  '0'
                }
                backgroundColor="#FFFFFF"
                textColor="#223466"
                legend={t('shipPage.horas')}
                icon={cardIcon}
                hasClassname
              />

              <SingleMetricCardWithIcon
                title={t('shipPage.productividad_grua')}
                detail={
                  shipPageData?.productividadBuque?.productividadGrua.toString() ??
                  '0'
                }
                backgroundColor="#FFFFFF"
                textColor="#223466"
                legend={t('shipPage.horas')}
                icon={cardIcon2}
              />

              <SingleMetricCardWithIcon
                title={t('shipPage.manos_productivas')}
                detail={
                  shipPageData?.productividadBuque?.manosProductivas.toString() ??
                  '0'
                }
                backgroundColor="#FFFFFF"
                textColor="#223466"
                icon={cardIcon3}
              />
            </>
          ) : null}
        </div>
        {viajesListForDropdown && viajesListForDropdown.length > 0 ? (
          <div className={styles.rightColumn}>
            <div>
              <DualSymbolCard
                title1={`${t('shipPage.emisiones')} (TONs)`}
                detail1={
                  shipPageData?.valoresCo2?.indicadoresCo2[0].valor.toString() ??
                  ''
                }
                title2={`${t('shipPage.emisiones')} (Kg/mov)`}
                detail2={
                  shipPageData?.valoresCo2?.indicadoresCo2[1].valor.toString() ??
                  ''
                }
                backgroundColor="#619b77"
                textColor="#FFFFFF"
                detailColor="#b9ffd3"
                symbol1="CO"
                symbolDetail1="2"
                symbol2="CO"
                symbolDetail2="2"
                icon={co2Icon}
              />
            </div>
            <div className={styles.historicalProductivityCard}>
              <ShipChartCard
                title={t('shipPage.productividad_historica')}
                legend={t('shipPage.utlimos_meses')}
                shipImage={shipIconChartCard}
                cardHeight={isMobile ? '315px' : '485px'}
                chartHeight={isMobile ? '190px' : '325px'}
                imgHeight="375px"
                chart={
                  <ThickBarChartWithoutAxes
                    data={
                      shipPageData?.productividadHistorica?.productividades ??
                      []
                    }
                  />
                }
              />
            </div>
          </div>
        ) : null}
      </div>
      {viajesListForDropdown && viajesListForDropdown.length > 0 ? (
        <div className={styles.dashboard}>
          <div className={styles.leftColumn}>
            <DualTextCard
              title1={t('shipPage.comienzo_operaciones')}
              detail1={formatDateAndTime(
                shipPageData?.productividadBuque?.inicio ?? ''
              )}
              title2={t('shipPage.fin_operaciones')}
              detail2={formatDateAndTime(
                shipPageData?.productividadBuque?.fin ?? ''
              )}
              backgroundColor="#4780bf"
              dividerColor="#223466"
              textColor="#FFFFFF"
              icon={dualTextCardIcon}
              className={classNames(styles.dualTextCardContainer)}
            />
          </div>

          <div className={classNames(styles.rightColumn)}>
            <div className={styles.cardContainerRigth}>
              <div className={styles.historicalProductivityCard}>
                <div className={styles.summary}>
                  <div className={styles.summaryItem}>
                    <SingleMetricCard
                      title={t('shipPage.total_contenedores_del_buque')}
                      detail={
                        shipPageData?.productividadBuque?.contenedores.toString() ??
                        '0'
                      }
                      backgroundColor="#8b3b83"
                      textColor="#FFFFFF"
                    />
                  </div>

                  <div className={styles.summaryItem}>
                    <SingleMetricCard
                      title={t('shipPage.movimiento_de_tapas')}
                      detail={
                        shipPageData?.productividadBuque?.movimientoTapas.toString() ??
                        '0'
                      }
                      backgroundColor="#629e77"
                      textColor="#FFFFFF"
                    />
                  </div>
                  <div className={styles.summaryItem}>
                    <SingleMetricCard
                      title={t('shipPage.horas_buque')}
                      detail={
                        shipPageData?.productividadBuque?.horasBuque.toString() ??
                        '0'
                      }
                      backgroundColor="#b26f00"
                      textColor="#FFFFFF"
                    />
                  </div>
                  <div className={styles.summaryItem}>
                    <SingleMetricCard
                      title={t('shipPage.horas_grua')}
                      detail={
                        shipPageData?.productividadBuque?.horasGrua.toString() ??
                        '0'
                      }
                      backgroundColor="#4780bf"
                      textColor="#FFFFFF"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.noData}>
          <h2>{t('terminalPage.no_data')}</h2>
        </div>
      )}
    </>
  );
};
